"use client";

import "swiper/css";
import "swiper/css/navigation";

import cn from "classnames";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { RichText } from "@/components/richText/richText.component";

import { SiteAnnouncementsQueryResult } from "@/sanity.types";

function markNavigationIconsDecorative(swiper: SwiperType) {
  const buttons = [swiper.navigation?.prevEl, swiper.navigation?.nextEl];

  for (const button of buttons) {
    if (!(button instanceof HTMLElement)) continue;

    const icon = button.querySelector("svg");
    icon?.setAttribute("aria-hidden", "true");
    icon?.setAttribute("focusable", "false");
  }
}

export const SiteAnnouncements = ({
  siteAnnouncements,
  className,
  variant = "gradient",
}: SiteAnnouncementsProps) => {
  const slideDuration = siteAnnouncements?.slideDuration || 3000;

  if (
    !siteAnnouncements?.active ||
    !siteAnnouncements?.announcementItems?.length
  ) {
    return null;
  }

  const renderItems = () =>
    siteAnnouncements?.announcementItems?.map((announcementItem, index) => (
      <SwiperSlide
        key={announcementItem._key}
        className="flex h-full min-h-0 w-full items-center justify-center overflow-hidden py-0 text-center"
        data-testid={`site-announcement-slide-${index}`}>
        <div className="flex h-full w-full max-w-full min-w-0 flex-col items-center justify-center px-8 sm:px-10">
          <div
            className="flex w-full min-w-0 flex-col items-center justify-center"
            data-testid={`site-announcement-message-${index}`}>
            {/* Short copy: smaller viewports */}
            <div
              className={cn(
                "wrap-break-words [**:text-white! block w-full min-w-0 text-center wrap-anywhere text-white **:data-[testid=text]:font-normal lg:hidden [&_a]:underline [&_a]:decoration-current",
              )}
              data-testid={`site-announcement-message-short-${index}`}>
              <RichText>
                {announcementItem.shortMessage ?? announcementItem.message}
              </RichText>
            </div>

            {/* Full copy: larger viewports */}
            <div
              className={cn(
                "wrap-break-words [**:text-white! hidden w-full min-w-0 text-center wrap-anywhere text-white **:data-[testid=text]:font-normal lg:block [&_a]:underline [&_a]:decoration-current",
              )}
              data-testid={`site-announcement-message-full-${index}`}>
              <RichText>{announcementItem.message}</RichText>
            </div>
          </div>
        </div>
      </SwiperSlide>
    ));

  return (
    <div
      data-testid="site-announcements"
      className={cn(
        "relative z-30 box-border grid h-14 w-full max-w-full min-w-0 shrink-0 grid-cols-1 grid-rows-[minmax(0,1fr)] overflow-x-clip overflow-y-hidden py-0",
        "relative isolate z-30 flex h-18 w-full max-w-full min-w-0 shrink-0 items-center overflow-x-clip overflow-y-hidden",
        className,
      )}>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0",
          variant === "solid" ? "bg-adt-amber" : "bg-secondary",
        )}
      />
      {variant === "gradient" ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(89deg,rgba(0,184,241,0.90)_-33.72%,rgba(7,64,139,0.90)_13.78%,var(--Primary-Secondary-Blue-200,rgba(0,34,106,0.90))_83.3%,var(--Primary-Primary-Blue-400,rgba(0,184,241,0.90))_120.86%)] bg-size-[100%_100%] bg-no-repeat"
        />
      ) : null}
      <div className="relative z-10 container mx-auto flex h-full min-h-0 w-full max-w-full min-w-0 items-center justify-center gap-2 px-3 sm:gap-3 sm:px-4">
        <Swiper
          modules={[Autoplay, Navigation, A11y]}
          // autoHeight={false}
          watchOverflow={true}
          a11y={{
            enabled: true,
            prevSlideMessage: "Previous announcement",
            nextSlideMessage: "Next announcement",
          }}
          onSwiper={markNavigationIconsDecorative}
          autoplay={{
            delay: slideDuration,
            waitForTransition: true,
            reverseDirection: false,
            stopOnLastSlide: false,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          navigation={true}
          data-testid="site-announcements-carousel"
          className={cn(
            "h-full min-h-0 min-w-0 flex-1 overflow-hidden py-0 [--swiper-navigation-size:14px] [&_.swiper]:h-full! [&_.swiper-notification]:sr-only [&_.swiper-slide]:h-full! [&_.swiper-wrapper]:h-full! [&_.swiper-wrapper]:items-center",
            {
              "[--swiper-navigation-color:var(--color-secondary)]":
                variant === "solid",
              "[--swiper-navigation-color:white]": variant === "gradient",
            },
          )}
          slidesPerView={1}
          loop={siteAnnouncements?.announcementItems?.length > 1}>
          {renderItems()}
        </Swiper>
      </div>
    </div>
  );
};

type SiteAnnouncementsProps = {
  siteAnnouncements: SiteAnnouncementsQueryResult | null | undefined;
  className?: string;
  variant?: "gradient" | "solid";
};
